// Tenant academic configuration (PRD-38 / ADR-0032).
//
// Mirrors the backend, which is canonical (magic-values.md §1):
//   • `Institution.PeriodType` / `Institution.GradeScale` — apps/backend/core/models.py
//   • `DEFAULT_PERIODS_PER_TYPE` / `MAX_PERIODS_PER_CYCLE` — apps/backend/core/constants.py
//
// The string values ARE the wire contract; the UI maps each to an es-MX /
// en-US label via `common:academic.*`. Period-type and scale labels are UI
// chrome, so unlike subject names (PRD-37) they ARE translated.

// `cuatrimestral` — PRD-61 FR-1.7 (three four-month terms a year, common in
// universities).
export const PERIOD_TYPES = ["anual", "semestral", "cuatrimestral", "trimestral"] as const
export type PeriodType = (typeof PERIOD_TYPES)[number]

export const GRADE_SCALES = ["0-10", "0-100"] as const
export type GradeScale = (typeof GRADE_SCALES)[number]

// Institution-level model defaults (backend migration lands every existing
// institution on exactly these).
export const DEFAULT_PERIOD_TYPE: PeriodType = "trimestral"
export const DEFAULT_GRADE_SCALE: GradeScale = "0-10"

// PRD-38 §FR-8 — the prefill the admin sees when they pick a period type.
// Shares its values with `core/constants.py::DEFAULT_PERIODS_PER_TYPE`, which
// the backend applies when `periods_per_cycle` is omitted on create, so the
// two stacks cannot drift.
//
// These are counts of periods across the WHOLE PROGRAM — not within one
// school year. A student is in semester 1, semester 3 or semester 9 *of their
// program*, never "the 1st semester of this year". The prefills below describe
// a 3-year Mexican secundaria (3 years = 3 periodos anuales = 6 semestres =
// 9 cuatrimestres = 9 trimestres); a prepa or universidad admin raises the
// number, up to MAX_PERIODS_PER_CYCLE.
export const DEFAULT_PERIODS_PER_TYPE: Record<PeriodType, number> = {
  anual: 3,
  semestral: 6,
  cuatrimestral: 9,
  trimestral: 9,
}

// Bounds enforced by the CheckConstraint on both models (PRD-38 §FR-3) and by
// the API (`422 periods_per_cycle_out_of_range`).
export const MIN_PERIODS_PER_CYCLE = 1
export const MAX_PERIODS_PER_CYCLE = 12

// The top of each scale. Used to render grades and to turn the presentation
// bands into ratios so a 0-100 institution is not judged against 0-10 lines.
export const GRADE_SCALE_MAX: Record<GradeScale, number> = {
  "0-10": 10,
  "0-100": 100,
}

// PRD-38 explicitly rules out rescaling values between scales, so there is no
// conversion helper here on purpose — a stored 8.4 on a 0-100 institution is a
// data problem to fix at the source, not something the UI silently multiplies.

// ── Evaluation calendar (PRD-61 FR-1) ──────────────────────────────────────
//
// Mirrors `EvaluationPeriod` in apps/backend/core/models.py (canonical): a
// school year is split into periods, each period into 1–6 cuts (parciales,
// trimestres or bimestres). The admin types the closing date of every cut
// but the last, which closes with the period itself.

export const CUT_NOUNS = ["parcial", "trimestre", "bimestre"] as const
export type CutNoun = (typeof CUT_NOUNS)[number]
export const DEFAULT_CUT_NOUN: CutNoun = "parcial"

// The API's `422 too_many_cuts` bound (PRD-61 FR-1.1: "1–6 cortes").
export const MIN_CUTS_PER_PERIOD = 1
export const MAX_CUTS_PER_PERIOD = 6
export const DEFAULT_CUTS_PER_PERIOD = 3
// `EvaluationPeriodIn.label` max_length (apps/backend/api/schemas.py).
export const MAX_PERIOD_LABEL_LENGTH = 80

// The "empezar con…" shortcut on an empty calendar: the SHAPE a school with
// this period type usually has — how many periods in a school year, how many
// cuts in each, and what they are called. Never dates: the admin types those
// (PRD-61 FR-1, "nunca inventar fechas"). Everything stays editable before
// the first save.
export const CALENDAR_QUICK_START: Record<
  PeriodType,
  { periods: number; cuts: number; noun: CutNoun }
> = {
  anual: { periods: 1, cuts: 3, noun: "trimestre" },
  semestral: { periods: 2, cuts: 3, noun: "parcial" },
  cuatrimestral: { periods: 3, cuts: 3, noun: "parcial" },
  trimestral: { periods: 3, cuts: 2, noun: "parcial" },
}
